import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './UserManagement.css';

interface User {
  id: string;
  email: string;
  role: 'participant' | 'organizer' | 'admin';
  name: string;
  phone?: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  dietaryRestrictions?: string[];
}

const UserManagement: React.FC = () => {
  const { accessToken } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<keyof User>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUsers(response.data.users || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke laste brukere');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return '#EF4444';
      case 'organizer': return '#3B82F6';
      case 'participant': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'organizer': return 'Arrangør';
      case 'participant': return 'Deltaker';
      default: return role;
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm));
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) return <div className="loading">Laster brukere...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>Brukeradministrasjon</h2>
        <div className="user-stats">
          <div className="stat-item">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Totalt brukere</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{users.filter(u => u.role === 'admin').length}</span>
            <span className="stat-label">Administratorer</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{users.filter(u => u.role === 'organizer').length}</span>
            <span className="stat-label">Arrangører</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{users.filter(u => u.role === 'participant').length}</span>
            <span className="stat-label">Deltakere</span>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Søk etter navn, e-post eller telefon..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="role-filter"
        >
          <option value="all">Alle roller</option>
          <option value="admin">Administratorer</option>
          <option value="organizer">Arrangører</option>
          <option value="participant">Deltakere</option>
        </select>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('role')} className="sortable">
                Rolle {sortField === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')} className="sortable">
                Navn {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                E-post {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Telefon</th>
              <th>Verifikasjon</th>
              <th>Status</th>
              <th onClick={() => handleSort('createdAt')} className="sortable">
                Opprettet {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Handling</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <span 
                    className="role-badge" 
                    style={{ backgroundColor: getRoleBadgeColor(user.role) }}
                  >
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td className="user-name">{user.name}</td>
                <td className="user-email">{user.email}</td>
                <td>{user.phone || '-'}</td>
                <td>
                  {user.emailVerified ? (
                    <span className="verified-badge">✓ Verifisert</span>
                  ) : (
                    <span className="unverified-badge">✗ Ikke verifisert</span>
                  )}
                </td>
                <td>
                  {user.isActive ? (
                    <span className="active-badge">Aktiv</span>
                  ) : (
                    <span className="inactive-badge">Inaktiv</span>
                  )}
                </td>
                <td className="created-date">
                  {new Date(user.createdAt).toLocaleDateString('nb-NO')}
                </td>
                <td>
                  <Link 
                    to={`/admin/users/${user.id}`} 
                    className="btn btn-small btn-secondary"
                  >
                    Rediger
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-results">
            Ingen brukere funnet som matcher søket.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
